import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, createStaticVNode as _createStaticVNode, withScopeId as _withScopeId, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-4aba33d6")
const _hoisted_1 = ["square", "noBorder"]
const _hoisted_2 = { class: "grid-table min-w-[480px]" }
const _hoisted_3 = /*#__PURE__*/_createStaticVNode("<div class=\"header\" data-v-4aba33d6><div class=\"ml-[24px] h-4\" data-v-4aba33d6>Action</div><div class=\"h-4\" data-v-4aba33d6>Value</div><div class=\"h-4\" data-v-4aba33d6>Tokens</div><div class=\"h-4\" data-v-4aba33d6>Time</div></div><div class=\"border\" data-v-4aba33d6></div>", 2)
const _hoisted_5 = ["href"]
const _hoisted_6 = { class: "flex w-full items-center" }
const _hoisted_7 = ["src"]
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "ml-[12px]" }
const _hoisted_10 = { class: "flex items-center justify-start" }
const _hoisted_11 = { class: "value-text mr-[16px]" }
const _hoisted_12 = { class: "flex items-center justify-start" }
const _hoisted_13 = {
  key: 0,
  class: "token-card mr-[8px] flex items-center text-center"
}
const _hoisted_14 = { class: "font-numeric mx-[7px]" }
const _hoisted_15 = { class: "flex items-center justify-start" }
_popScopeId()

import { getAddress } from '@ethersproject/address';
import { formatDistanceToNow } from 'date-fns';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { ColumnDefinition } from '@/components/_global/BalTable/BalTable.vue';
import useBreakpoints from '@/composables/useBreakpoints';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import useTokens from '@/composables/useTokens';
import { bnum } from '@/lib/utils';
import { PoolActivity, PoolActivityType } from '@/services/pool/types';
import useWeb3 from '@/services/web3/useWeb3';

import plus from './plus.svg';
import minus from './minus.svg';

/**
 * TYPES
 */
type TokenAmount = {
  address: string;
  symbol: string;
  amount: string;
};

type ActivityRow = {
  label: string;
  formattedValue: string;
  value: number;
  timestamp: number;
  formattedDate: string;
  tx: string;
  type: PoolActivityType;
  tokenAmounts: TokenAmount[];
};

type Props = {
  tokens: string[];
  poolActivities: PoolActivity[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  loadMore?: () => void;
  isPaginated?: boolean;
  noResultsLabel?: string;
};


export default _defineComponent({
  props: {
    tokens: { type: Array, required: true },
    poolActivities: { type: Array, required: true },
    isLoading: { type: Boolean, required: false, default: false },
    isLoadingMore: { type: Boolean, required: false, default: false },
    loadMore: { type: Function, required: false },
    isPaginated: { type: Boolean, required: false, default: false },
    noResultsLabel: { type: String, required: false, default: '' }
  } as unknown as undefined,
  emits: ['loadMore'],
  setup(__props: {
  tokens: string[];
  poolActivities: PoolActivity[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  loadMore?: () => void;
  isPaginated?: boolean;
  noResultsLabel?: string;
}, { emit }) {

const props = __props
const { isMobile, isDesktop } = useBreakpoints();

/**
 * PROPS & EMITS
 */




/**
 * COMPOSABLES
 */
const { fNum2 } = useNumbers();
const { t } = useI18n();
const { explorerLinks } = useWeb3();
const { upToLargeBreakpoint } = useBreakpoints();
const { getToken, priceFor } = useTokens();
/**
 * COMPUTED
 */
const columns = computed<ColumnDefinition<ActivityRow>[]>(() => [
  {
    name: t('action'),
    id: 'action',
    accessor: 'tx',
    Cell: 'actionCell',
    width: 150,
    sortable: false,
  },
  {
    name: t('value'),
    id: 'value',
    accessor: 'value',
    Cell: 'valueCell',
    align: 'right',
    className: 'align-center w-40',
    sortKey: pool => pool.value,
    width: 125,
  },
  {
    name: t('tokens'),
    id: 'details',
    accessor: '',
    Cell: 'detailsCell',
    width: 325,
    sortable: false,
  },
  {
    name: t('time'),
    id: 'timeAgo',
    accessor: 'timestamp',
    Cell: 'timeCell',
    align: 'right',
    sortKey: pool => pool.timestamp,
    width: 200,
  },
]);

const activityRows = computed<ActivityRow[]>(() =>
  props.isLoading
    ? []
    : props.poolActivities.map(({ type, timestamp, tx, amounts }) => {
        const isJoin = type === 'Join';
        const value = getJoinExitValue(amounts);

        return {
          label: isJoin ? t('invest') : t('withdraw.label'),
          value,
          formattedValue:
            value > 0
              ? fNum2(value, { style: 'currency', abbreviate: true })
              : '-',
          timestamp,
          formattedDate: t('timeAgo', [formatDistanceToNow(timestamp)]),
          tx,
          type,
          tokenAmounts: getJoinExitDetails(amounts),
        };
      })
);

/**
 * METHODS
 */
function getJoinExitValue(amounts: PoolActivity['amounts']) {
  let total = bnum(0);

  for (let i = 0; i < amounts.length; i++) {
    const amount = amounts[i];
    const address = getAddress(props.tokens[i]);
    const token = getToken(address);
    const price = priceFor(token?.address);
    const amountNumber = Math.abs(parseFloat(amount));
    // If the price is unknown for any of the positive amounts - the value cannot be computed.
    if (amountNumber > 0 && price === 0) {
      return 0;
    }
    total = total.plus(bnum(amountNumber).times(price));
  }
  return total.toNumber();
}

function getJoinExitDetails(amounts: PoolActivity['amounts']) {
  return amounts.map((amount, index) => {
    const address = getAddress(props.tokens[index]);
    const token = getToken(address);
    const symbol = token ? token.symbol : address;
    const amountNumber = parseFloat(amount);
    return {
      address,
      symbol,
      amount: fNum2(amountNumber, FNumFormats.token),
    };
  });
}

// var len = Math.min(5, activityRows.value.length);
var len = 0;
function load_more() {
  if (len + 5 >= activityRows.value.length) {
    len = activityRows.value.length;
  } else {
    len += 5;
  }
}

const getRows = computed<ActivityRow[]>(() => {
  return activityRows.value.slice(0, len);
});

return (_ctx: any,_cache: any) => {
  const _component_BalAsset = _resolveComponent("BalAsset")!

  return (_openBlock(), _createElementBlock("div", {
    class: "table-container overflow-x-auto p-[24px]",
    square: _unref(upToLargeBreakpoint),
    noBorder: _unref(upToLargeBreakpoint),
    noPad: ""
  }, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(activityRows), (action, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "flex table-row w-full items-center"
        }, [
          _createElementVNode("a", {
            href: _unref(explorerLinks).txLink(action.tx),
            class: "pool-row my-[18px] flex w-full items-center",
            target: "_blank"
          }, [
            _createElementVNode("div", _hoisted_6, [
              (action.label == 'Invest')
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    src: _unref(plus)
                  }, null, 8, _hoisted_7))
                : (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: _unref(minus)
                  }, null, 8, _hoisted_8)),
              _createElementVNode("div", _hoisted_9, _toDisplayString(action.label), 1)
            ])
          ], 8, _hoisted_5),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, _toDisplayString(action.formattedValue), 1)
          ]),
          _createElementVNode("div", _hoisted_12, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(action.tokenAmounts, (tokenAmount, i) => {
              return (_openBlock(), _createElementBlock("div", { key: i }, [
                (tokenAmount.amount !== '0')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createVNode(_component_BalAsset, {
                        size: 16,
                        address: tokenAmount.address,
                        class: ""
                      }, null, 8, ["address"]),
                      _createElementVNode("div", _hoisted_14, _toDisplayString(tokenAmount.amount), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_15, _toDisplayString(action.formattedDate), 1)
        ]))
      }), 128))
    ])
  ], 8, _hoisted_1))
}
}

})